"use client";
import React, { useEffect, useState, useMemo } from "react";

import {
  ResponsiveDrawerDropdown,
  DropdownToggle,
  DropdownItem,
} from "@stadium/ui/src/components/bootstrap-dropdown";

import { getSelectedLanguage } from "@stadium/services/weglot/helpers";
import { weglotNoTranslationClassName } from "@stadium/constants";

import styles from "./main.module.scss";

interface LanguageItem {
  key: string; // Weglot language code e.g. 'en', 'fr'
  label: string;
}

interface LanguageSelectorProps {
  className?: string;
  availableLanguages: LanguageItem[];
  weglotInstance: any;
  onSelectLang: (langId: string) => void;
  langInUrl?: string;
  title?: string;
  labelText?: React.ReactNode;
  arrowCustomClass?: string;
}

const LanguageSelector = (props: LanguageSelectorProps) => {
  const {
    className = "",
    availableLanguages = [],
    weglotInstance,
    onSelectLang,
    langInUrl,
    title = "Translate Page To",
    labelText,
    arrowCustomClass = "",
  } = props;

  const [selectedLang, setSelectedLang] = useState<string>("");

  useEffect(() => {
    if (availableLanguages.length && weglotInstance) {
      const languageToBeSelected = getSelectedLanguage({
        weglotLanguages: availableLanguages,
        langInUrl,
        weglotInstance,
      });
      if (languageToBeSelected) {
        const currentLang = weglotInstance?.getCurrentLang?.();
        if (currentLang && currentLang !== languageToBeSelected) {
          weglotInstance?.switchTo?.(languageToBeSelected);
        }
        setSelectedLang(languageToBeSelected);
      }
    }
  }, [availableLanguages, weglotInstance, langInUrl]);

  const handleLanguageSelection = (languageToBeSelected: string) => {
    if (!languageToBeSelected || languageToBeSelected === selectedLang) return;
    weglotInstance?.switchTo?.(languageToBeSelected);
    onSelectLang?.(languageToBeSelected);
    setSelectedLang(languageToBeSelected);
  };

  const selectedIndex = useMemo(() => {
    const idx = availableLanguages.findIndex((opt) => opt.key === selectedLang);
    return idx >= 0 ? idx : 0;
  }, [availableLanguages, selectedLang]);

  const currentLabel = availableLanguages[selectedIndex]?.label || "Language";

  return (
    <ResponsiveDrawerDropdown
      label={
        <DropdownToggle
          className={`${styles.languageToggle} ${className}`}
          dropDownIconClass={arrowCustomClass}
          headingStyle="no-style"
          styleType="no-border"
        >
          <>
            {labelText}
            <span className={styles.currentLabel}>{currentLabel}</span>
          </>
        </DropdownToggle>
      }
      heading={
        <DropdownToggle headingStyle="default" className={styles.languageHeading}>
          {title}
        </DropdownToggle>
      }
      align="left"
      width="narrow"
      className={weglotNoTranslationClassName}
    >
      {availableLanguages.map((lang) => (
        <DropdownItem
          key={lang.key}
          onClick={() => handleLanguageSelection(lang.key)}
          isActive={lang.key === selectedLang}
          className={styles.languageItem}
        >
          {lang.label}
        </DropdownItem>
      ))}
    </ResponsiveDrawerDropdown>
  );
};

export default LanguageSelector;
